import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import styled from 'styled-components';

import 'react-dates/initialize';
import {
  DayPickerRangeController, isInclusivelyAfterDay, isInclusivelyBeforeDay, isSameDay,
} from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';

import { COLOR_BLACK } from '../../constants/colors';
import { breakpoints } from '../../constants/mediaqueries';
import { fontStyles, underlinedButtonStyles } from '../../constants/styles';


// Minimum horizontal travel (px) for a touch gesture to count as a month swipe.
const SWIPE_THRESHOLD = 50;


/* Popover container for /book-direct. Day cells are styled site-wide by
   global.css (red text on black bg via !important), matching the rooms-page
   BookingWidget calendar, so no per-cell overrides live here.

   Mobile: the check-out field is too narrow to anchor a 340px calendar
   without it running off-screen, so we centre it as a fixed viewport panel
   (mirroring the rooms-page BookingWidget's mobile treatment). Desktop drops
   it under the field as usual. */
const Popover = styled.div`
  position: fixed;
  z-index: 3;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 2rem);
  max-width: 340px;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  padding: 0.75em;
  border-radius: 0.5em;
  background-color: ${COLOR_BLACK};
  color: #f85365;
  ${fontStyles.bodySmall}

  @media ${breakpoints.l} {
    position: absolute;
    z-index: 2;
    top: calc(100% + 0.5rem);
    left: 0;
    transform: none;
    width: 340px;
    max-width: none;
    max-height: none;
    overflow-y: visible;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const FooterButton = styled.button`
  ${underlinedButtonStyles}
  color: #f85365;

  &::before {
    background-color: #f85365;
  }
`;


const DateRangePopover = ({
  ariaLabel, arrivalDate, blockedDays, departureDate, focusedInput,
  onClear, onDatesChange, onFocusChange,
}) => {
  const yesterday = moment().subtract(1, 'days');
  const containerRef = React.useRef(null);
  const touchOrigin = React.useRef(null);

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    touchOrigin.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event) => {
    if (!touchOrigin.current) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchOrigin.current.x;
    const deltaY = touch.clientY - touchOrigin.current.y;
    touchOrigin.current = null;

    // Act only on a deliberate horizontal swipe; vertical drags stay free to
    // scroll the popover on short screens, and small moves stay taps.
    if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    // Drive react-dates' own month navigation so we reuse its slide animation
    // and bounds. Buttons render prev-then-next in DOM order.
    const buttons = containerRef.current
      && containerRef.current.querySelectorAll('.DayPickerNavigation_button');
    if (!buttons || buttons.length < 2) return;

    const target = deltaX < 0 ? buttons[1] : buttons[0];
    if (target) target.click();
  };

  return (
    <Popover
      aria-label={ariaLabel}
      innerRef={containerRef}
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchStart}
      role="dialog"
    >
      <DayPickerRangeController
        daySize={40}
        endDate={departureDate}
        focusedInput={focusedInput}
        initialVisibleMonth={() => arrivalDate || departureDate || moment()}
        isDayBlocked={(day1) => (focusedInput === 'startDate' && blockedDays.some((day2) => isSameDay(day1, day2)))}
        isOutsideRange={(day) => isInclusivelyBeforeDay(day, yesterday) || isInclusivelyAfterDay(day, moment().add(1, 'year'))}
        numberOfMonths={1}
        onDatesChange={onDatesChange}
        onFocusChange={onFocusChange}
        startDate={arrivalDate}
      />
      <Footer>
        <FooterButton onClick={onClear} type="button">
          <FormattedMessage defaultMessage="Clear" id="booking.inputClear" />
        </FooterButton>
      </Footer>
    </Popover>
  );
};

DateRangePopover.propTypes = {
  ariaLabel: PropTypes.string,
  arrivalDate: PropTypes.instanceOf(moment),
  blockedDays: PropTypes.array.isRequired,
  departureDate: PropTypes.instanceOf(moment),
  focusedInput: PropTypes.string.isRequired,
  onClear: PropTypes.func.isRequired,
  onDatesChange: PropTypes.func.isRequired,
  onFocusChange: PropTypes.func.isRequired,
};

DateRangePopover.defaultProps = {
  ariaLabel: 'Select your stay',
  arrivalDate: null,
  departureDate: null,
};

export default DateRangePopover;
